import { fromPromise, setup, and } from 'xstate';
import {
  Application,
  GeneratedDisclosures,
  Offer,
} from '@ads-bread/shared/bread/codecs';
import { ActorErrorParams } from '../../../apply/lib/types';
import {
  ApplicationMachineEvents,
  SendApplicationCheckoutParams,
  SendApplicationPrepareCheckoutParams,
  SendAssignSelectedOfferParams,
  SendCreateApplicationParams,
  SendFetchApplicationParams,
  SendReCreateApplicationParams,
  SendReCreateContingentInStoreApplicationParams,
} from './types/events';
import {
  assignApplication,
  assignApplicationWithAppendedStatusCodes,
  assignChildMachineReady,
  assignDeferredInitializationEvent,
  assignGeneratedDisclosures,
  assignSelectedOffer,
  resetSelectedOffer,
  assignStateMachineActorRefs,
  clearDeferredInitializationEvents,
  resetContext,
} from './assigns';
import {
  ApplicationCheckoutResult,
  ApplicationPrepareCheckoutResult,
  CreateApplicationResult,
  EvaluateApplicationErrorResult,
  FetchApplicationResult,
  FetchLatestApplicationResult,
  LocateExchangedOfferAndGenerateDisclosuresResult,
  PrepareInStoreApplicationResult,
  PrepareVirtualCardIssuanceResult,
  ReCreateApplicationResult,
  ReCreateInStoreContingentApplicationResult,
} from './types/actors';
import {
  getApplicationCheckoutInput,
  getApplicationPrepareCheckoutInput,
  getCreateApplicationInput,
  getEvaluateApplicationErrorInput,
  getFetchApplicationInput,
  getFetchLatestApplicationInput,
  getGenerateDisclosuresInput,
  getPrepareInStoreApplicationInput,
  getPrepareVirtualCadIssuanceInput,
  getReCreateApplicationInput,
  getReCreateInStoreContingentApplicationInput,
} from './inputs';
import {
  getApplicationCheckoutCompleteDelayParams,
  getApplicationCheckoutParams,
  getApplicationPrepareCheckoutParams,
  getAssignChildMachineReadyParams,
  getAssignDeferredInitializationEventParams,
  getAssignGeneratedDisclosuresParams,
  getAssignSelectedOfferParams,
  getCreateApplicationParams,
  getEvaluateApplicationErrorParams,
  getFetchApplicationParams,
  getFetchLatestApplicationParams,
  getLocateExchangedOfferAndGenerateDisclosuresParams,
  getPrepareInStoreApplicationParams,
  getReCreateApplicationParams,
  getServiceErrorParams,
} from './params';
import { EstimatedSpendMachineActorRef } from './machines/estimatedSpend/estimatedSpendMachine';
import { FraudAlloyMachineActorRef } from './machines/fraudAlloy/useFraudAlloyMachineLogic';
import { PaymentsMachineActorRef } from './machines/payments/usePaymentsMachineLogic';

export type StateMachineActorRefs = {
  estimatedSpendMachineActorRef: EstimatedSpendMachineActorRef | null;
  fraudAlloyMachineActorRef: FraudAlloyMachineActorRef | null;
  paymentsMachineActorRef: PaymentsMachineActorRef | null;
};

export interface ApplicationContext {
  application: Application | null;
  selectedOffer: Offer | null;
  generatedDisclosures: GeneratedDisclosures | null;
  stateMachineActorRefs: StateMachineActorRefs;
  childMachinesReady: Set<string>;
  childMachinesRequired: string[];
  deferredInitializationEvents: ApplicationMachineEvents[];
}

export const applicationMachine = setup({
  types: {
    context: {} as ApplicationContext,
    events: {} as ApplicationMachineEvents,
    input: {} as ApplicationContext,
  },
  guards: {
    isInstallmentOfferSelected: (_, __: SendAssignSelectedOfferParams) => {
      throw new Error('Not implemented');
    },
    isSplitPayOfferSelected: (_, __: SendAssignSelectedOfferParams) => {
      throw new Error('Not implemented');
    },
    isHybridLineOfCreditOfferSelected: (
      _,
      __: SendAssignSelectedOfferParams
    ) => {
      throw new Error('Not implemented');
    },
    isUnexpiredInStoreApplication: (_, __: FetchLatestApplicationResult) => {
      throw new Error('Not implemented');
    },
    isIneligibleAddress: (_, __: FetchLatestApplicationResult) => {
      throw new Error('Not implemented');
    },
    needsFullIIN: (
      _,
      __:
        | FetchLatestApplicationResult
        | CreateApplicationResult
        | ReCreateApplicationResult
    ) => {
      throw new Error('Not implemented');
    },
    isValidApplicationAndMatchesPreviouslyApprovedOrder: (
      _,
      __: FetchLatestApplicationResult
    ) => {
      throw new Error('Not implemented');
    },
    hasExchangedPaymentAgreementId: (_, __: FetchLatestApplicationResult) => {
      throw new Error('Not implemented');
    },
    isBuyerReadyAndComplete: (): boolean => {
      throw new Error('Not implemented');
    },
    isInvalidApplication: (_, __: FetchLatestApplicationResult) => {
      throw new Error('Not implemented');
    },
    isCompleteMismatchedBuyer: () => {
      throw new Error('Not implemented');
    },
    isApproved: (
      _,
      __:
        | CreateApplicationResult
        | ReCreateApplicationResult
        | ReCreateInStoreContingentApplicationResult
    ) => {
      throw new Error('Not implemented');
    },
    isApprovedContingentInStore: (
      _,
      __: CreateApplicationResult | FetchLatestApplicationResult
    ) => {
      throw new Error('Not implemented');
    },
    needsIDVerificationAlloy: (
      _,
      __:
        | CreateApplicationResult
        | ReCreateApplicationResult
        | ApplicationCheckoutResult
        | ApplicationPrepareCheckoutResult
    ) => {
      throw new Error('Not implemented');
    },
    isCheckoutCompleted: (_, __: ApplicationCheckoutResult) => {
      throw new Error('Not implemented');
    },
    isDownPaymentAuthDeclinedError: (_, __: ApplicationCheckoutResult) => {
      throw new Error('Not implemented');
    },
    isCheckoutPrepared: (_, __: ApplicationPrepareCheckoutResult) => {
      throw new Error('Not implemented');
    },
    isErrorResult: (_, __: ApplicationPrepareCheckoutResult) => {
      throw new Error('Not implemented');
    },
    isVirtualCardApplication: (): boolean => {
      throw new Error('Not implemented');
    },
    isPoBoxAddressIneligible: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isInvalidAddressNoMatchIneligible: (
      _,
      __: EvaluateApplicationErrorResult
    ) => {
      throw new Error('Not implemented');
    },
    isBuyerHashFailed: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isCreditFreeze: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isKycDenial: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isPreviousDenialIneligible: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isMaxCardIneligible: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isOutstandingLoansIneligible: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isAgeIneligible: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isAgeAlabamaMilitary: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isBuyerStatusIneligible: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isBuyerSkippedInstallments: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isLocationIneligible: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isNeedsActionWarning: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isNeedsAdditionalInformationWarning: (
      _,
      __: EvaluateApplicationErrorResult
    ) => {
      throw new Error('Not Implemented');
    },
    isFraudDenial: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isCreditDenial: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isSanctionsOFACDenial: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isSanctionsDenial: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isFraudAlertDenial: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isCapacityRecheck: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    isAlloySystemOutage: (_, __: EvaluateApplicationErrorResult) => {
      throw new Error('Not implemented');
    },
    requiredChildMachinesReady: () => {
      throw new Error('Not implemented');
    },
  },
  actions: {
    assignApplication,
    assignApplicationWithAppendedStatusCodes,
    assignGeneratedDisclosures,
    assignSelectedOffer,
    resetSelectedOffer,
    assignStateMachineActorRefs,
    assignChildMachineReady,
    assignDeferredInitializationEvent,
    clearDeferredInitializationEvents,
    resetContext,
    stopStateMachineActorRefs: () => {
      throw new Error('Not implemented');
    },
    handleApplicationDone: (
      _,
      __:
        | FetchApplicationResult
        | ReCreateApplicationResult
        | ApplicationCheckoutResult
        | ApplicationPrepareCheckoutResult
    ) => {
      throw new Error('Not implemented');
    },
    handleApplicationServiceError: (_, __: ActorErrorParams): void => {
      throw new Error('Not implemented');
    },
    handleApplicationAnalytics: (
      _,
      __:
        | FetchLatestApplicationResult
        | CreateApplicationResult
        | ApplicationCheckoutResult
        | ApplicationPrepareCheckoutResult
    ) => {
      throw new Error('Not implemented');
    },
    handleApplicationCheckoutCompleteAnalytics: (
      _,
      __: ApplicationCheckoutResult
    ) => {
      throw new Error('Not implemented');
    },
    handleApplicationOrderSubmittedAnalytics: (): void => {
      throw new Error('Not implemented');
    },
    handleAutoPayEnrollmentSuccess: (_, __: ApplicationCheckoutResult) => {
      throw new Error('Not implemented');
    },
    handleSelectedOfferAnalytics: (_, __: SendAssignSelectedOfferParams) => {
      throw new Error('Not implemented');
    },
    handleOnApproved: (_, __: CreateApplicationResult) => {
      throw new Error('Not implemented');
    },
    handleOnCheckout: (_, __: ApplicationContext) => {
      throw new Error('Not implemented');
    },
    hasReviewedApplication: (_, __: FetchLatestApplicationResult) => {
      throw new Error('Not implemented');
    },
    emitCreatedApplicationEstimatedSpend: (_, __: CreateApplicationResult) => {
      throw new Error('Not implemented');
    },
    emitFetchedApplicationEstimatedSpend: (
      _,
      __: FetchLatestApplicationResult | FetchApplicationResult
    ) => {
      throw new Error('Not implemented');
    },
    processDeferredInitializationEvents: () => {
      throw new Error('Not implemented');
    },
    logError: (_, __: ActorErrorParams): void => {
      throw new Error('Not implemented');
    },
  },
  actors: {
    fetchApplication: fromPromise<
      FetchApplicationResult,
      SendFetchApplicationParams
    >(() => {
      throw new Error('Not implemented');
    }),
    fetchLatestApplication: fromPromise<FetchLatestApplicationResult>(() => {
      throw new Error('Not implemented');
    }),
    createApplication: fromPromise<
      CreateApplicationResult,
      SendCreateApplicationParams
    >(() => {
      throw new Error('Not implemented');
    }),
    reCreateApplication: fromPromise<
      ReCreateApplicationResult,
      SendReCreateApplicationParams
    >(() => {
      throw new Error('Not implemented');
    }),
    applicationCheckout: fromPromise<
      ApplicationCheckoutResult,
      SendApplicationCheckoutParams
    >(() => {
      throw new Error('Not implemented');
    }),
    applicationPrepareCheckout: fromPromise<
      ApplicationPrepareCheckoutResult,
      SendApplicationPrepareCheckoutParams
    >(() => {
      throw new Error('Not implemented');
    }),
    prepareInStoreApplication: fromPromise<
      PrepareInStoreApplicationResult,
      ApplicationContext
    >(() => {
      throw new Error('Not implemented');
    }),
    prepareVirtualCardIssuance: fromPromise<
      PrepareVirtualCardIssuanceResult,
      ApplicationContext
    >(() => {
      throw new Error('Not implemented');
    }),
    locateExchangedOfferAndGenerateDisclosures: fromPromise<
      LocateExchangedOfferAndGenerateDisclosuresResult,
      ApplicationContext
    >(() => {
      throw new Error('Not implemented');
    }),
    reCreateInStoreContingentApplication: fromPromise<
      ReCreateInStoreContingentApplicationResult,
      SendReCreateContingentInStoreApplicationParams
    >(() => {
      throw new Error('Not implemented');
    }),
    getErrorStatusCodeFromApplication: fromPromise<
      EvaluateApplicationErrorResult,
      unknown
    >(() => {
      throw new Error('Not implemented');
    }),
  },
  delays: {
    checkoutCompleteDelay: () => {
      throw new Error('Not implemented');
    },
  },
}).createMachine({
  id: 'application',
  initial: 'initial',
  context: ({ input }) => {
    return {
      application: input.application || null,
      selectedOffer: input.selectedOffer || null,
      generatedDisclosures: input.generatedDisclosures || null,
      stateMachineActorRefs: {
        estimatedSpendMachineActorRef: null,
        fraudAlloyMachineActorRef: null,
        paymentsMachineActorRef: null,
      },
      childMachinesReady: new Set(),
      childMachinesRequired: ['fraudAlloyMachine'],
      deferredInitializationEvents: [],
    };
  },
  on: {
    SEND_RESET_APPLICATION_STATE: {
      actions: [
        {
          type: 'stopStateMachineActorRefs',
        },
        {
          type: 'resetContext',
        },
      ],
      target: '.initial',
    },
  },
  states: {
    initial: {
      entry: ['assignStateMachineActorRefs'],
      always: {
        guard: 'requiredChildMachinesReady',
        actions: [
          'processDeferredInitializationEvents',
          'clearDeferredInitializationEvents',
        ],
        target: 'ready',
      },
      on: {
        SEND_CHILD_MACHINE_READY: {
          actions: [
            {
              type: 'assignChildMachineReady',
              params: getAssignChildMachineReadyParams,
            },
          ],
        },
        '*': {
          actions: [
            {
              type: 'assignDeferredInitializationEvent',
              params: getAssignDeferredInitializationEventParams,
            },
          ],
        },
      },
    },
    ready: {
      on: {
        SEND_FETCH_APPLICATION: {
          target: 'fetchingApplication',
        },
        SEND_FETCH_LATEST_APPLICATION: {
          target: 'fetchingLatestApplication',
        },
        SEND_CREATE_APPLICATION: {
          target: 'creatingApplication',
        },
        SEND_RECREATE_APPLICATION: {
          target: 'reCreatingApplication',
        },
        SEND_APPLICATION_CHECKOUT: {
          target: 'creatingApplicationCheckout',
        },
        SEND_APPLICATION_PREPARE_CHECKOUT: {
          target: 'creatingApplicationPrepareCheckout',
        },
        SEND_ASSIGN_GENERATED_DISCLOSURES: {
          actions: [
            {
              type: 'assignGeneratedDisclosures',
              params: getAssignGeneratedDisclosuresParams,
            },
          ],
        },
      },
      initial: 'initial',
      states: {
        initial: {},
        approved: {
          initial: 'initial',
          on: {
            SEND_ASSIGN_SELECTED_OFFER: [
              {
                guard: {
                  type: 'isInstallmentOfferSelected',
                  params: getAssignSelectedOfferParams,
                },
                actions: [
                  {
                    type: 'assignSelectedOffer',
                    params: getAssignSelectedOfferParams,
                  },
                  {
                    type: 'handleSelectedOfferAnalytics',
                    params: getAssignSelectedOfferParams,
                  },
                ],
                target: '.selected.installment',
              },
              {
                guard: {
                  type: 'isSplitPayOfferSelected',
                  params: getAssignSelectedOfferParams,
                },
                actions: [
                  {
                    type: 'assignSelectedOffer',
                    params: getAssignSelectedOfferParams,
                  },
                  {
                    type: 'handleSelectedOfferAnalytics',
                    params: getAssignSelectedOfferParams,
                  },
                ],
                target: '.selected.splitPay',
              },
              {
                guard: {
                  type: 'isHybridLineOfCreditOfferSelected',
                  params: getAssignSelectedOfferParams,
                },
                actions: [
                  {
                    type: 'assignSelectedOffer',
                    params: getAssignSelectedOfferParams,
                  },
                  {
                    type: 'handleSelectedOfferAnalytics',
                    params: getAssignSelectedOfferParams,
                  },
                ],
                target: '.selected.hybridLineOfCredit',
              },
              {
                target: '.selected.initial',
              },
            ],
          },
          states: {
            initial: {},
            idVerified: {},
            withExchangedPaymentAgreement: {},
            // In Store returning buyer with application states
            inStore: {
              initial: 'initial',
              states: {
                initial: {},
                prepared: {},
                virtualCard: {},
              },
            },
            // Determined selected offer
            selected: {
              initial: 'initial',
              states: {
                initial: {},
                splitPay: {},
                installment: {},
                hybridLineOfCredit: {},
              },
            },
          },
        },
        needsFullIIN: {
          on: {
            // Override top level event. We want to manually re-create this application
            SEND_FETCH_LATEST_APPLICATION: {},
          },
        },
        needsIDVerificationAlloy: {
          on: {
            SEND_ID_UPLOAD_JOURNEY_SUCCESS: {
              target: '#application.ready.approved.idVerified',
            },
            SEND_ID_UPLOAD_JOURNEY_FAILURE: {
              target: '#application.errors.idVerificationFailure',
            },
            SEND_ID_UPLOAD_JOURNEY_PENDING: {
              target: '#application.errors.idVerificationPending',
            },
          },
          initial: 'initial',
          states: {
            initial: {},
          },
        },
        // No route events are associated with this state. Use when you want a wait state
        recoverableError: {},
        invalid: {},
        checkoutCompleted: {
          initial: 'initial',
          states: {
            initial: {
              after: {
                checkoutCompleteDelay: {
                  actions: [
                    {
                      type: 'handleOnCheckout',
                      params: getApplicationCheckoutCompleteDelayParams,
                    },
                  ],
                  target: 'final',
                },
              },
            },
            final: {},
          },
        },
        checkoutPrepared: {},
      },
    },
    fetchingApplication: {
      invoke: {
        src: 'fetchApplication',
        id: 'fetchApplication',
        input: getFetchApplicationInput,
        onDone: [
          {
            actions: [
              {
                type: 'assignApplication',
                params: getFetchApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getFetchApplicationParams,
              },
              {
                type: 'handleApplicationDone',
                params: getFetchApplicationParams,
              },
              {
                type: 'emitFetchedApplicationEstimatedSpend',
                params: getFetchApplicationParams,
              },
            ],
            target: 'ready.approved',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'handleApplicationServiceError',
                params: getServiceErrorParams,
              },
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'ready.initial',
          },
        ],
      },
    },
    fetchingLatestApplication: {
      invoke: {
        src: 'fetchLatestApplication',
        id: 'fetchLatestApplication',
        input: getFetchLatestApplicationInput,
        onDone: [
          {
            guard: {
              type: 'isUnexpiredInStoreApplication',
              params: getFetchLatestApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'hasReviewedApplication',
                params: getFetchLatestApplicationParams,
              },
            ],
            target: 'preparingInStoreApplication',
          },
          {
            guard: {
              type: 'isIneligibleAddress',
              params: getFetchLatestApplicationParams,
            },
            target: 'errors.poBoxAddressIneligible',
          },
          {
            guard: {
              type: 'needsFullIIN',
              params: getFetchLatestApplicationParams,
            },
            target: 'ready.needsFullIIN',
          },
          {
            guard: {
              type: 'isApprovedContingentInStore',
              params: getFetchLatestApplicationParams,
            },
            actions: [
              {
                type: 'hasReviewedApplication',
                params: getFetchLatestApplicationParams,
              },
            ],
            target: 'reCreatingInStoreContingentApplication',
          },
          {
            guard: and([
              {
                type: 'hasExchangedPaymentAgreementId',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'isValidApplicationAndMatchesPreviouslyApprovedOrder',
                params: getFetchLatestApplicationParams,
              },
            ] as never[]),
            actions: [
              {
                type: 'assignApplication',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'emitFetchedApplicationEstimatedSpend',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'hasReviewedApplication',
                params: getFetchLatestApplicationParams,
              },
            ],
            target: 'locatingExchangedOfferAndGeneratingDisclosures',
          },
          {
            guard: {
              type: 'isValidApplicationAndMatchesPreviouslyApprovedOrder',
              params: getFetchLatestApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'emitFetchedApplicationEstimatedSpend',
                params: getFetchLatestApplicationParams,
              },
              {
                type: 'hasReviewedApplication',
                params: getFetchLatestApplicationParams,
              },
            ],
            target: 'ready.approved',
          },
          {
            guard: {
              type: 'isCompleteMismatchedBuyer',
              params: getFetchLatestApplicationParams,
            },
            target: 'creatingApplication',
          },
          {
            guard: {
              type: 'isInvalidApplication',
              params: getFetchLatestApplicationParams,
            },
            target: 'ready.invalid',
          },
          {
            guard: {
              type: 'isBuyerReadyAndComplete',
              params: getFetchLatestApplicationParams,
            },
            actions: [
              {
                type: 'hasReviewedApplication',
                params: getFetchLatestApplicationParams,
              },
            ],
            target: 'creatingApplication',
          },
          {
            target: 'ready.invalid',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'ready.initial',
          },
        ],
      },
    },
    preparingInStoreApplication: {
      invoke: {
        src: 'prepareInStoreApplication',
        input: getPrepareInStoreApplicationInput,
        onDone: [
          {
            guard: {
              type: 'isVirtualCardApplication',
              params: getPrepareInStoreApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getPrepareInStoreApplicationParams,
              },
              {
                type: 'emitFetchedApplicationEstimatedSpend',
                params: getPrepareInStoreApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getPrepareInStoreApplicationParams,
              },
            ],
            target: 'preparingVirtualCardApplication',
          },
          {
            actions: [
              {
                type: 'assignApplication',
                params: getPrepareInStoreApplicationParams,
              },
              {
                type: 'emitFetchedApplicationEstimatedSpend',
                params: getPrepareInStoreApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getPrepareInStoreApplicationParams,
              },
            ],
            target: 'ready.approved.inStore.prepared',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'errors.unknown',
          },
        ],
      },
    },
    preparingVirtualCardApplication: {
      invoke: {
        src: 'prepareVirtualCardIssuance',
        input: getPrepareVirtualCadIssuanceInput,
        onDone: [
          {
            target: 'ready.approved.inStore.virtualCard',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'errors.unknown',
          },
        ],
      },
    },
    locatingExchangedOfferAndGeneratingDisclosures: {
      invoke: {
        src: 'locateExchangedOfferAndGenerateDisclosures',
        input: getGenerateDisclosuresInput,
        onDone: [
          {
            actions: [
              {
                type: 'assignGeneratedDisclosures',
                params: getLocateExchangedOfferAndGenerateDisclosuresParams,
              },
              {
                type: 'assignSelectedOffer',
                params: getLocateExchangedOfferAndGenerateDisclosuresParams,
              },
            ],
            target: 'ready.approved.withExchangedPaymentAgreement',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'ready.approved',
          },
        ],
      },
    },
    creatingApplication: {
      entry: ['resetSelectedOffer'],
      invoke: {
        src: 'createApplication',
        input: getCreateApplicationInput,
        onDone: [
          {
            guard: {
              type: 'isApprovedContingentInStore',
              params: getCreateApplicationParams,
            },
            target: 'reCreatingInStoreContingentApplication',
          },
          {
            guard: {
              type: 'isApproved',
              params: getCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getCreateApplicationParams,
              },
              {
                type: 'emitCreatedApplicationEstimatedSpend',
                params: getCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getCreateApplicationParams,
              },
              {
                type: 'handleOnApproved',
                params: getCreateApplicationParams,
              },
            ],
            target: 'ready.approved',
          },
          {
            guard: {
              type: 'needsFullIIN',
              params: getCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getCreateApplicationParams,
              },
            ],
            target: 'ready.needsFullIIN',
          },
          {
            guard: {
              type: 'needsIDVerificationAlloy',
              params: getCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplicationWithAppendedStatusCodes',
                params: getCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getCreateApplicationParams,
              },
            ],
            target: 'ready.needsIDVerificationAlloy',
          },
          {
            target: 'evaluateApplicationError',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'errors.unknown',
          },
        ],
      },
    },
    reCreatingInStoreContingentApplication: {
      entry: ['resetSelectedOffer'],
      invoke: {
        src: 'reCreateInStoreContingentApplication',
        input: getReCreateInStoreContingentApplicationInput,
        onDone: [
          {
            guard: {
              type: 'isApproved',
              params: getCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getCreateApplicationParams,
              },
              {
                type: 'emitCreatedApplicationEstimatedSpend',
                params: getCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getCreateApplicationParams,
              },
              {
                type: 'handleOnApproved',
                params: getCreateApplicationParams,
              },
            ],
            target: 'ready.approved',
          },
          {
            guard: {
              type: 'needsFullIIN',
              params: getCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getCreateApplicationParams,
              },
            ],
            target: 'ready.needsFullIIN',
          },
          {
            guard: {
              type: 'needsIDVerificationAlloy',
              params: getCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplicationWithAppendedStatusCodes',
                params: getCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getCreateApplicationParams,
              },
            ],
            target: 'ready.needsIDVerificationAlloy',
          },
          {
            target: 'evaluateApplicationError',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'errors.unknown',
          },
        ],
      },
    },
    reCreatingApplication: {
      entry: ['resetSelectedOffer'],
      invoke: {
        src: 'reCreateApplication',
        input: getReCreateApplicationInput,
        onDone: [
          {
            guard: {
              type: 'isApproved',
              params: getReCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getReCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getReCreateApplicationParams,
              },
              {
                type: 'handleOnApproved',
                params: getReCreateApplicationParams,
              },
              {
                type: 'handleApplicationDone',
                params: getReCreateApplicationParams,
              },
            ],
            target: 'ready.approved',
          },
          {
            guard: {
              type: 'needsFullIIN',
              params: getCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getReCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getReCreateApplicationParams,
              },
              {
                type: 'handleApplicationDone',
                params: getReCreateApplicationParams,
              },
            ],
            target: 'ready.needsFullIIN',
          },
          {
            guard: {
              type: 'needsIDVerificationAlloy',
              params: getReCreateApplicationParams,
            },
            actions: [
              {
                type: 'assignApplicationWithAppendedStatusCodes',
                params: getReCreateApplicationParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getReCreateApplicationParams,
              },
              {
                type: 'handleApplicationDone',
                params: getReCreateApplicationParams,
              },
            ],
            target: 'ready.needsIDVerificationAlloy',
          },
          {
            actions: [
              {
                type: 'handleApplicationDone',
                params: getReCreateApplicationParams,
              },
            ],
            target: 'evaluateApplicationError',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'handleApplicationServiceError',
                params: getServiceErrorParams,
              },
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'errors.unknown',
          },
        ],
      },
    },
    creatingApplicationCheckout: {
      entry: [
        {
          type: 'handleApplicationOrderSubmittedAnalytics',
        },
      ],
      invoke: {
        src: 'applicationCheckout',
        input: getApplicationCheckoutInput,
        onDone: [
          {
            guard: {
              type: 'isCheckoutCompleted',
              params: getApplicationCheckoutParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getApplicationCheckoutParams,
              },
              {
                type: 'handleApplicationCheckoutCompleteAnalytics',
                params: getApplicationCheckoutParams,
              },
              {
                type: 'handleAutoPayEnrollmentSuccess',
                params: getApplicationCheckoutParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getApplicationCheckoutParams,
              },
              {
                type: 'handleApplicationDone',
                params: getApplicationCheckoutParams,
              },
            ],
            target: 'ready.checkoutCompleted',
          },
          {
            guard: {
              type: 'needsIDVerificationAlloy',
              params: getApplicationCheckoutParams,
            },
            actions: [
              {
                type: 'assignApplicationWithAppendedStatusCodes',
                params: getApplicationCheckoutParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getApplicationCheckoutParams,
              },
              {
                type: 'handleApplicationDone',
                params: getApplicationCheckoutParams,
              },
            ],
            target: 'ready.needsIDVerificationAlloy',
          },
          {
            guard: {
              type: 'isDownPaymentAuthDeclinedError',
              params: getApplicationCheckoutParams,
            },
            actions: [
              {
                type: 'handleApplicationDone',
                params: getApplicationCheckoutParams,
              },
            ],
            target: 'ready.recoverableError',
          },
          {
            actions: [
              {
                type: 'handleApplicationDone',
                params: getApplicationCheckoutParams,
              },
            ],
            target: 'evaluateApplicationError',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'handleApplicationServiceError',
                params: getServiceErrorParams,
              },
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'errors.unknown',
          },
        ],
      },
    },
    creatingApplicationPrepareCheckout: {
      invoke: {
        src: 'applicationPrepareCheckout',
        input: getApplicationPrepareCheckoutInput,
        onDone: [
          {
            guard: {
              type: 'isCheckoutPrepared',
              params: getApplicationPrepareCheckoutParams,
            },
            actions: [
              {
                type: 'assignApplication',
                params: getApplicationPrepareCheckoutParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getApplicationPrepareCheckoutParams,
              },
              {
                type: 'handleApplicationDone',
                params: getApplicationPrepareCheckoutParams,
              },
            ],
            target: 'ready.checkoutPrepared',
          },
          {
            guard: {
              type: 'needsIDVerificationAlloy',
              params: getApplicationPrepareCheckoutParams,
            },
            actions: [
              {
                type: 'assignApplicationWithAppendedStatusCodes',
                params: getApplicationPrepareCheckoutParams,
              },
              {
                type: 'handleApplicationAnalytics',
                params: getApplicationPrepareCheckoutParams,
              },
              {
                type: 'handleApplicationDone',
                params: getApplicationPrepareCheckoutParams,
              },
            ],
            target: 'ready.needsIDVerificationAlloy',
          },
          {
            guard: {
              type: 'isErrorResult',
              params: getApplicationPrepareCheckoutParams,
            },
            actions: [
              {
                type: 'handleApplicationDone',
                params: getApplicationPrepareCheckoutParams,
              },
            ],
            target: 'ready.recoverableError',
          },
          {
            actions: [
              {
                type: 'handleApplicationDone',
                params: getApplicationPrepareCheckoutParams,
              },
            ],
            target: 'evaluateApplicationError',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'handleApplicationServiceError',
                params: getServiceErrorParams,
              },
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'errors.unknown',
          },
        ],
      },
    },
    evaluateApplicationError: {
      invoke: {
        src: 'getErrorStatusCodeFromApplication',
        input: getEvaluateApplicationErrorInput,
        onDone: [
          {
            guard: {
              type: 'isPoBoxAddressIneligible',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.poBoxAddressIneligible',
          },
          {
            guard: {
              type: 'isInvalidAddressNoMatchIneligible',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.invalidAddressNoMatchIneligible',
          },
          {
            guard: {
              type: 'isBuyerHashFailed',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.buyerHashFailed',
          },
          {
            guard: {
              type: 'isCreditFreeze',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.creditFreeze',
          },
          {
            guard: {
              type: 'isKycDenial',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.kycDenial',
          },
          {
            guard: {
              type: 'isPreviousDenialIneligible',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.previousDenialIneligible',
          },
          {
            guard: {
              type: 'isMaxCardIneligible',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.maxCardIneligible',
          },
          {
            guard: {
              type: 'isOutstandingLoansIneligible',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.outstandingLoansIneligible',
          },
          {
            guard: {
              type: 'isAgeIneligible',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.ageIneligible',
          },
          {
            guard: {
              type: 'isAgeAlabamaMilitary',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.ageAlabamaMilitary',
          },
          {
            guard: {
              type: 'isBuyerStatusIneligible',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.buyerStatusIneligible',
          },
          {
            guard: {
              type: 'isBuyerSkippedInstallments',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.buyerSkippedInstallments',
          },
          {
            guard: {
              type: 'isLocationIneligible',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.locationIneligible',
          },
          {
            guard: {
              type: 'isNeedsActionWarning',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.needsActionWarning',
          },
          {
            guard: {
              type: 'isNeedsAdditionalInformationWarning',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.needsAdditionalInformationWarning',
          },
          {
            guard: {
              type: 'isFraudDenial',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.fraudDenial',
          },
          {
            guard: {
              type: 'isCreditDenial',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.creditDenial',
          },
          {
            guard: {
              type: 'isSanctionsOFACDenial',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.sanctionsOFACDenial',
          },
          {
            guard: {
              type: 'isSanctionsDenial',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.sanctionsDenial',
          },
          {
            guard: {
              type: 'isFraudAlertDenial',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.fraudAlertDenial',
          },
          {
            guard: {
              type: 'isCapacityRecheck',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.capacityRecheck',
          },
          {
            guard: {
              type: 'isAlloySystemOutage',
              params: getEvaluateApplicationErrorParams,
            },
            target: 'errors.alloySystemOutage',
          },
          {
            target: 'errors.unknown',
          },
        ],
        onError: [
          {
            target: 'errors.unknown',
          },
        ],
      },
    },
    errors: {
      initial: 'unknown',
      states: {
        poBoxAddressIneligible: {
          on: {
            SEND_CREATE_APPLICATION: {
              target: '#application.creatingApplication',
            },
            SEND_FETCH_LATEST_APPLICATION: {
              target: '#application.fetchingLatestApplication',
            },
          },
        },
        invalidAddressNoMatchIneligible: {
          on: {
            SEND_CREATE_APPLICATION: {
              target: '#application.creatingApplication',
            },
            SEND_FETCH_LATEST_APPLICATION: {
              target: '#application.fetchingLatestApplication',
            },
          },
        },
        buyerHashFailed: {
          on: {
            SEND_RECREATE_APPLICATION: {
              target: '#application.reCreatingApplication',
            },
          },
        },
        creditFreeze: {},
        kycDenial: {},
        previousDenialIneligible: {},
        maxCardIneligible: {},
        outstandingLoansIneligible: {},
        ageIneligible: {},
        ageAlabamaMilitary: {},
        buyerStatusIneligible: {},
        buyerSkippedInstallments: {},
        locationIneligible: {},
        needsActionWarning: {},
        needsAdditionalInformationWarning: {},
        fraudDenial: {},
        creditDenial: {},
        sanctionsOFACDenial: {},
        sanctionsDenial: {},
        fraudAlertDenial: {},
        idVerificationFailure: {},
        idVerificationPending: {},
        alloySystemOutage: {},
        capacityRecheck: {
          on: {
            SEND_RECREATE_APPLICATION: {
              target: '#application.reCreatingApplication',
            },
          },
        },
        unknown: {},
      },
    },
  },
});
